import { useQuery } from '@apollo/client';
import { 
  GET_CREATIVE_PACKAGE, 
  CREATIVE_PACKAGES_BY_CREATIVE_ID,
  LIST_CREATIVE_PACKAGES,
  GET_CREATIVE_PACKAGE_BOOKING_DETAILS
} from '../../graphql/queries/creativeQueries';
import { useMemo } from 'react';
import { getCloudFrontImageUrl } from '../../util/cloudFrontUtils';
import awsConfig from '../../config/aws-config';

/**
 * Hook to fetch a single Creative Package by ID
 * @param {string} packageId - The Package ID
 * @returns {object} - { package, loading, error }
 */
export const useCreativePackage = (packageId) => {
  const { data, loading, error } = useQuery(GET_CREATIVE_PACKAGE, {
    variables: { id: packageId },
    skip: !packageId,
    fetchPolicy: 'cache-and-network',
  });

  const processedPackage = useMemo(() => {
    if (!data?.getCreativePackage) return null;

    const pkg = { ...data.getCreativePackage };

    // Process portfolio images
    if (pkg.portfolioImages && Array.isArray(pkg.portfolioImages)) {
      pkg.processedPortfolioImages = pkg.portfolioImages.map(imageKey => {
        if (!imageKey) return '';
        try {
          return getCloudFrontImageUrl(imageKey, {
            width: 800,
            height: 600,
            fit: 'cover'
          });
        } catch (error) {
          try {
            const cleanKey = imageKey.startsWith('public/') ? imageKey : `public/${imageKey}`;
            return `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
          } catch (fallbackError) {
            return '';
          }
        }
      });
    }

    // Process creative profile picture
    if (pkg.creative?.profilePicture) {
      let processedProfilePicture = "";
      try {
        processedProfilePicture = getCloudFrontImageUrl(pkg.creative.profilePicture, {
          width: 200,
          height: 200,
          fit: 'cover'
        });
      } catch (error) {
        processedProfilePicture = "";
      }
      pkg.creative = {
        ...pkg.creative,
        processedProfilePicture
      };
    }

    return pkg;
  }, [data]);

  return {
    package: processedPackage,
    loading,
    error
  };
};

/**
 * Hook to fetch a single Creative Package by ID
 * @param {string} packageId - The Package ID
 * @returns {object} - { package, loading, error }
 */
export const useCreativePackageBookingDetails = (packageId) => {
  const { data, loading, error } = useQuery(GET_CREATIVE_PACKAGE_BOOKING_DETAILS, {
    variables: { id: packageId },
    skip: !packageId,
    fetchPolicy: 'cache-and-network',
  });

  const processedPackage = useMemo(() => {
    if (!data?.getCreativePackage) return null;

    const pkg = { ...data.getCreativePackage };

    // Process only the first portfolio image
    if (pkg.portfolioImages && Array.isArray(pkg.portfolioImages) && pkg.portfolioImages.length > 0) {
      const firstImageKey = pkg.portfolioImages[0];
      if (firstImageKey) {
        try {
          pkg.firstPortfolioImage = getCloudFrontImageUrl(firstImageKey, {
            width: 800,
            height: 600,
            fit: 'cover'
          });
        } catch (error) {
          try {
            const cleanKey = firstImageKey.startsWith('public/') ? firstImageKey : `public/${firstImageKey}`;
            pkg.firstPortfolioImage = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
          } catch (fallbackError) {
            pkg.firstPortfolioImage = '';
          }
        }
      } else {
        pkg.firstPortfolioImage = '';
      }
    } else {
      pkg.firstPortfolioImage = '';
    }

    return pkg;
  }, [data]);

  return {
    package: processedPackage,
    loading,
    error
  };
}

/**
 * Hook to fetch all Packages for a specific Creative
 * @param {string} creativeId - The Creative ID
 * @param {object} options - Query options (filter, sortDirection, limit)
 * @returns {object} - { packages, loading, error, hasPackages }
 */
export const useCreativePackagesByCreativeId = (creativeId, options = {}) => {
  const { data, loading, error } = useQuery(CREATIVE_PACKAGES_BY_CREATIVE_ID, {
    variables: {
      creativeID: creativeId,
      filter: options.filter,
      sortDirection: options.sortDirection || 'ASC',
      limit: options.limit || 100,
      nextToken: options.nextToken
    },
    skip: !creativeId,
    fetchPolicy: 'cache-and-network',
  });

  const processedData = useMemo(() => {
    if (!data?.creativePackagesByCreativeID?.items) return null;

    const packages = data.creativePackagesByCreativeID.items.map(pkg => {
      const processedPackage = { ...pkg };

      // Process portfolio images
      if (processedPackage.portfolioImages && Array.isArray(processedPackage.portfolioImages)) {
        processedPackage.processedPortfolioImages = processedPackage.portfolioImages.map(imageKey => {
          if (!imageKey) return '';
          try {
            return getCloudFrontImageUrl(imageKey, {
              width: 800,
              height: 600,
              fit: 'cover'
            });
          } catch (error) {
            return '';
          }
        });
      }

      return processedPackage;
    });

    return {
      packages: packages,
      nextToken: data.creativePackagesByCreativeID.nextToken,
      hasPackages: packages.length > 0
    };
  }, [data]);

  return {
    packagesData: processedData,
    loading,
    error
  };
};

/**
 * Hook to list all Creative Packages with optional filters
 * @param {object} options - Query options (filter, limit, nextToken)
 * @returns {object} - { packages, loading, error }
 */
export const useListCreativePackages = (options = {}) => {
  const { data, loading, error } = useQuery(LIST_CREATIVE_PACKAGES, {
    variables: {
      filter: options.filter,
      limit: options.limit || 100,
      nextToken: options.nextToken
    },
    fetchPolicy: 'cache-and-network',
  });

  const processedData = useMemo(() => {
    if (!data?.listCreativePackages?.items) return null;

    const packages = data.listCreativePackages.items.map(pkg => {
      const processedPackage = { ...pkg };

      // Process portfolio images
      if (processedPackage.portfolioImages && Array.isArray(processedPackage.portfolioImages)) {
        processedPackage.processedPortfolioImages = processedPackage.portfolioImages.map(imageKey => {
          if (!imageKey) return '';
          try {
            return getCloudFrontImageUrl(imageKey, {
              width: 800,
              height: 600,
              fit: 'cover'
            });
          } catch (error) {
            return '';
          }
        });
      }

      // Process creative profile picture
      if (processedPackage.creative?.profilePicture) {
        let processedProfilePicture = "";
        try {
          processedProfilePicture = getCloudFrontImageUrl(processedPackage.creative.profilePicture, {
            width: 200,
            height: 200,
            fit: 'cover'
          });
        } catch (error) {
          processedProfilePicture = "";
        }
        processedPackage.creative = {
          ...processedPackage.creative,
          processedProfilePicture
        };
      }

      return processedPackage;
    });

    return {
      packages: packages,
      nextToken: data.listCreativePackages.nextToken
    };
  }, [data]);

  return {
    packages: processedData?.packages || [],
    nextToken: processedData?.nextToken,
    loading,
    error
  };
};


